import { isEqual, orderBy, pick } from 'lodash-es';
import { type StateCreator } from 'zustand';
import { getSafeErrorFields } from '../errors';
import { type AudioGraphStore } from './types';

const STORAGE_KEY = 'heidi-audio-device-id';
const NO_DEVICE_TIMEOUT = 15000;

export interface DeviceSlice {
  device: MediaDeviceInfo | null;
  availableDevices: MediaDeviceInfo[];
  noDeviceTimeout: NodeJS.Timeout | null;
  loadInputDevices: () => Promise<MediaDeviceInfo[]>;
  changeDeviceId: (deviceId: string, signal?: AbortSignal) => Promise<void>;
  checkDevices: (signal?: AbortSignal) => Promise<void>;
  switchDevices: (signal?: AbortSignal) => Promise<boolean>;
  loadDeviceFromStorage: () => Promise<void>;
  interrupt: () => void;
  pickDevice: (devices?: MediaDeviceInfo[]) => boolean;
}

export const createDeviceSlice: StateCreator<AudioGraphStore, [], [], DeviceSlice> = (
  set,
  get
) => {
  return {
    device: null,
    availableDevices: [],
    noDeviceTimeout: null,

    checkDevices: async (signal) => {
      const { noDeviceTimeout, loadInputDevices, switchDevices, availableDevices } =
        get();
      const newAvailableDevices = await loadInputDevices();
      if (signal?.aborted) throw signal.reason;

      const deviceStillAvailable = newAvailableDevices.some(
        (d) => d.deviceId === get().device?.deviceId && d.label === get().device?.label
      );

      const lostDeviceInUse =
        availableDevices.length > newAvailableDevices.length && !deviceStillAvailable;
      const gainedDeviceFromNone =
        newAvailableDevices.length > 0 && availableDevices.length == 0;

      if (lostDeviceInUse || gainedDeviceFromNone) {
        const ok = await switchDevices(signal);
        if (!ok && !noDeviceTimeout) {
          const newNoDeviceTimeout = setTimeout(() => {
            get().interrupt();
            get().logError({ event_type: 'NO_AUDIO_DEVICES_INTERRUPTION' });
          }, NO_DEVICE_TIMEOUT);
          set({ noDeviceTimeout: newNoDeviceTimeout });
        } else if (noDeviceTimeout) {
          clearTimeout(noDeviceTimeout);
          set({ noDeviceTimeout: null });
        }
      }
    },

    switchDevices: async (signal) => {
      if (signal?.aborted) throw signal.reason;
      const ok = get().pickDevice();
      // Switching devices is a fresh mic attempt: a stale DEVICE_IN_USE from
      // the previous device must not gate the rebuild on the new one.
      get().clearError('DEVICE_IN_USE');
      // Auto-switch has no awaiter and bypasses the task queue's error log, so
      // catch here: a busy/vanishing picked device would otherwise surface only
      // as an unhandled rejection.
      try {
        await get().rebuildGraph(undefined, signal);
      } catch (err) {
        if (signal?.aborted) throw signal.reason;
        get().logError({
          event_type: 'SWITCH_DEVICES_REBUILD_FAILED',
          details: getSafeErrorFields(err),
        });
      }
      return ok;
    },

    loadInputDevices: async () => {
      const { addError, clearError, testMic } = get();

      try {
        await testMic();
      } catch {
        return [];
      }

      const devices = await navigator.mediaDevices.enumerateDevices();

      const newAvailableDevices = orderBy(
        devices.filter((device) => device.kind === 'audioinput'),
        [
          (device) => (device.deviceId === 'default' ? 0 : 1),
          (device) => device.label || '',
        ]
      );

      if (newAvailableDevices.length === 0) {
        addError('NO_DEVICES');
        get().logWarn({ event_type: 'NO_AUDIO_DEVICES_ON_INIT' });
      } else {
        clearError('NO_DEVICES');
      }

      const devicesChanged = !isEqual(get().availableDevices, newAvailableDevices);
      if (devicesChanged) set({ availableDevices: newAvailableDevices });
      return newAvailableDevices;
    },

    changeDeviceId: async (deviceId: string, signal?: AbortSignal) => {
      if (signal?.aborted) throw signal.reason;
      const device = get().availableDevices.find((d) => d.deviceId === deviceId) ?? null;
      set({ device });

      get().logInfo({
        event_type: 'AUDIO_DEVICE_CHANGED',
        details: pick(device, ['deviceId', 'label']),
      });

      if (device) localStorage.setItem(STORAGE_KEY, device?.deviceId);
      get().clearError('DEVICE_IN_USE');
      await get().rebuildGraph(undefined, signal);
    },

    loadDeviceFromStorage: async () => {
      const devices = await get().loadInputDevices();
      const savedDeviceId = localStorage.getItem(STORAGE_KEY);

      const device = savedDeviceId
        ? devices.find((d) => d.deviceId === savedDeviceId)
        : null;

      if (device) {
        set({ device });
      } else {
        get().pickDevice(devices);
      }
    },

    pickDevice: (devices: MediaDeviceInfo[] = get().availableDevices) => {
      const device = devices.find((d) => d.deviceId === 'default') ?? devices[0] ?? null;
      set({ device });
      get().logInfo({ event_type: 'AUDIO_DEVICE_CHANGED_TO_DEFAULT' });
      return Boolean(device);
    },

    interrupt: () => {
      get().listeners.forEach((l) => l.onInterruption?.());
      get().cleanupDeviceListeners();
      get().cleanupMicSource();
      get().cleanupTabSource();
      get().addError('INTERRUPTED');
    },
  };
};
